<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div
    ref="moinTeaserRef"
    class="moin-teaser teaser-background"
    :class="{ mobileteaser: mobileTeaser, [getAlignmentOrigin]: true }"
  >
    <transition-showtext :on-after-appear="forceRepaint">
      <div v-if="teaserText" :id="teaserTextId" class="moin-teaser-message" aria-hidden="true">
        {{ teaserText }}
      </div>
    </transition-showtext>
    <transition-showquickreplies :on-after-appear="forceRepaint">
      <div
        class="moin-teaser-qrlist"
        role="group"
        :aria-labelledby="teaserText ? teaserTextId : undefined"
      >
        <ButtonPill
          v-for="(intent, index) in getQuickActions"
          :key="index"
          :with-shadow="true"
          @click="handleQuickReplyClick(intent)"
        >
          {{ intent.text }}
        </ButtonPill>
      </div>
    </transition-showquickreplies>
    <div class="moin-teaser-closebutton">
      <transition-showclosebutton :on-after-appear="forceRepaint">
        <button :aria-label="t('teaser.close')" @click="closeTeaser">
          <IconCloseTeaser />
        </button>
      </transition-showclosebutton>
    </div>
  </div>
</template>

<script setup lang="ts">
import IconCloseTeaser from "@/svg/IconCloseTeaser.vue";
import { templateRef, useElementBounding } from "@vueuse/core";
import { computed, onMounted, ref, useId, watch } from "vue";
import ButtonPill from "@/components/common/ButtonPill.vue";
import type { Intent } from "@/types";
import transitionShowtext from "./transition-showtext.vue";
import transitionShowquickreplies from "./transition-showquickreplies.vue";
import transitionShowclosebutton from "./transition-showclosebutton.vue";

import { useI18n } from "@/composables/useI18n";
import { useForceRepaint } from "@/composables/useForceRepaint";

const props = withDefaults(
  defineProps<{
    teaserText?: string | undefined;
    quickreplies: readonly Intent[] | undefined;
    isMobileTeaser?: boolean;
    alignmentOrigin: string;
  }>(),
  { isMobileTeaser: false, teaserText: undefined }
);

const emit = defineEmits<{
  onClose: [];
  onIntent: [intent: Intent];
}>();

const dimensions = defineModel<{
  height: string;
  width: string;
}>();

const moinTeaserRef = templateRef("moinTeaserRef");
const { triggerRepaint } = useForceRepaint(moinTeaserRef);

const teaserTextId = useId();
const { t } = useI18n();

// handle teaser dimensions
// watch changes and update to upper iframe so it can adjust the iframe size
const { width, height } = useElementBounding(moinTeaserRef);
watch([width, height], () => {
  dimensions.value = {
    height: moinTeaserRef.value.getBoundingClientRect().height + 10 + "px",
    width: moinTeaserRef.value.getBoundingClientRect().width + 10 + "px",
  };
});

const getQuickActions = computed(() => props.quickreplies);
const mobileTeaser = computed(() => props.isMobileTeaser);
const getAlignmentOrigin = computed(() =>
  props.alignmentOrigin === "left" ? "alignment-left" : ""
);

const closeTeaser = () => emit("onClose");
// prevent double clicking the teaser
const clickGuard = ref(false);
const handleQuickReplyClick = (intent: Intent) => {
  if (clickGuard.value) return;
  clickGuard.value = true;
  // reset the click guard after 500ms
  // we reuse the teaser component
  // so if force the teaser open, it needs to be reset

  setTimeout(() => {
    clickGuard.value = false;
  }, 500);
  emit("onIntent", intent);
};

// this is a hack to force a repaint of the element
// Webkit has a bug where it doesn't repaint the element after the element
// and creates weird artifacts with the drop shadow filter
// this is not pretty but it works for now
const forceRepaint = () => {
  triggerRepaint(10);
};

onMounted(() => {
  dimensions.value = {
    height: moinTeaserRef.value.getBoundingClientRect().height + 10 + "px",
    width: moinTeaserRef.value.getBoundingClientRect().width + 10 + "px",
  };
  forceRepaint();
});
</script>

<style lang="scss">
:root {
  --teaser-content: 100px;
}

.moin-teaser {
  will-change: transform, opacity;
  position: absolute;
  right: 0;
  bottom: 0px;
  display: grid;
  row-gap: 15px;
  padding-right: 90px;
  padding-top: 20px;
  padding-left: 20px;
  padding-bottom: 30px;
  max-width: 450px;
  overflow: hidden;
  transform: translateZ(0);
  opacity: 1;
  transition: opacity 0.1s ease-in-out;
}

.teaser-background {
  transition: background-color 1.5s 0.5s ease-in-out;
  background: rgba(226, 226, 226, 0);

  &:hover {
    background: rgba(226, 226, 226, 0.8);
    /* background: rgba(226, 0, 0, 0.8); */
    transition: background-color 0.2s 0s ease-in-out;
  }

  border-radius: 10px;
}

.moin-teaser-message {
  filter: drop-shadow(0 0 10px $kw-default-shadow-color-light);
  position: relative;
  background: white;
  max-width: 400px;
  padding: 12px 10px 10px;

  text-align: left;
  border-radius: var(--kw-widget-global-borderradius-outer);
  transform-origin: 100% 100%;

  align-self: end;
  justify-self: end;
}

.moin-teaser-qrlist {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-flow: row wrap;
  gap: 10px;
  row-gap: 5px;

  /* If no QRs, ensure it artificially lift box so close button does not collide with avatar */
  min-height: 14px;
}

.moin-teaser-closebutton {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  right: 5px;
  top: 0px;
  min-height: 48px;
  min-width: 48px;

  filter: drop-shadow(0 0 10px $kw-default-shadow-color-light);
  will-change: transform;

  button {
    background: transparent;
    outline: 0;
    border: none;
    border-radius: 48px;

    @include default-focus-unknown-bg;
    padding: 0; /* Padding exists out of nowhere on x axis. Makes tab-focus outline look wonky */

    &:hover {
      cursor: pointer;
    }
  }
}

.moin-teaser.alignment-left {
  padding-left: 80px;
  padding-right: 20px;

  .moin-teaser-message {
    justify-self: start;
    margin-right: 45px;
  }

  .moin-teaser-qrlist {
    justify-content: flex-start;
  }
}

/* -------------------- */
/* TEASER MOBILE STYLES */
/* -------------------- */
.moin-teaser.mobileteaser {
  width: 100%;
  max-width: 100%;

  &.teaser-background {
    transition: bottom 0.5s ease-in-out, right 0.5s ease-in-out, background-color 0s 0s ease-in-out;
    background: rgba(226, 226, 226, 0.8);
    border-bottom-right-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    padding-top: 10px;
    padding-left: 10px;
  }
}

.moin-teaser.alignment-left.mobileteaser {
  .moin-teaser-message {
    margin-left: 90px;
  }

  .moin-teaser-qrlist {
    margin-left: 90px;
  }
}
</style>
